import { useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../context/AuthContext";

let cache = { client: null, staff: null };
let inflight = { client: null, staff: null };

const fetchPerms = (kind) => {
  if (cache[kind]) return Promise.resolve(cache[kind]);
  if (!inflight[kind]) {
    const path = kind === "staff" ? "/settings/staff-permissions" : "/settings/client-permissions";
    inflight[kind] = api.get(path).then((r) => { cache[kind] = r.data?.permissions || {}; return cache[kind]; })
      .catch(() => ({})).finally(() => { inflight[kind] = null; });
  }
  return inflight[kind];
};

export const invalidateClientPerms = () => { cache = { client: null, staff: null }; };

// Single source of truth for area access in the UI (backend remains authoritative).
// Clients follow the client matrix by client_role; staff follow the staff matrix by staff_role (master = unrestricted).
export function useClientPerms() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const isStaff = user?.role === "super_admin";
  const staffRole = user?.staff_role || "master";
  const role = isClient ? (user?.client_role || "admin") : staffRole;
  const kind = isClient ? "client" : "staff";
  const needsMatrix = isClient || (isStaff && staffRole !== "master");
  const [perms, setPerms] = useState(cache[kind]);

  useEffect(() => {
    if (needsMatrix && !perms) fetchPerms(kind).then(setPerms);
  }, [needsMatrix, kind, perms]);

  const loaded = !needsMatrix || !!perms;
  const todayIso = new Date().toISOString().slice(0, 10);
  const rawOv = (isStaff && user?.perm_overrides) || {};
  const ov = Object.fromEntries(Object.entries(rawOv).filter(([, v]) => !v?.expires_on || v.expires_on >= todayIso));
  const base = (area, k) => (loaded ? !!perms?.[role]?.[area]?.[k] : false);
  const canView = (area) => !area || !needsMatrix || (typeof ov[area]?.view === "boolean" ? ov[area].view : base(area, "view"));
  const canEdit = (area) => !needsMatrix ? true : (typeof ov[area]?.edit === "boolean" ? (ov[area].edit && ov[area].view !== false) : base(area, "edit"));
  return { isClient, role, loaded, perms, canView, canEdit };
}
